import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, Compass, ExternalLink, CheckCircle2, AlertTriangle, XCircle, Target, Sparkles, Download, Save } from 'lucide-react';
import { pathways } from '@/lib/api';

const ENGLISH = [
  { v: 'competent', l: 'Competent (IELTS 6.0)' },
  { v: 'proficient', l: 'Proficient (IELTS 7.0)' },
  { v: 'superior', l: 'Superior (IELTS 8.0)' },
];
const QUALIFICATIONS = ['Certificate III', 'Diploma', 'Bachelor', 'Masters', 'PhD'];
const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
const ASSESSMENT = ['not_started', 'in_progress', 'positive', 'negative'];

const VERDICT_ICON = { yes: CheckCircle2, likely: AlertTriangle, no: XCircle };
const VERDICT_TONE = {
  yes: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  likely: 'border-amber-300 bg-amber-50 text-amber-700',
  no: 'border-slate-200 bg-slate-50 text-slate-500',
};

export default function PathwayAdvisorPage() {
  const [form, setForm] = React.useState({
    age: 26, current_visa: 'Student 500', onshore: true,
    occupation: '', anzsco: '', qualification: 'Bachelor',
    english: 'proficient', work_experience_years: 1,
    australian_study_years: 2, australian_work_years: 0.5,
    regional_study: false, regional_work: false,
    state: 'QLD', skills_assessment_status: 'in_progress',
    partner_skills: false, has_employer_sponsor: false,
  });
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [narrative, setNarrative] = React.useState(null);
  const [genBusy, setGenBusy] = React.useState(false);

  const analyse = async () => {
    setBusy(true); setResult(null); setNarrative(null);
    try {
      const r = await pathways.analyse(form);
      setResult(r);
    } finally { setBusy(false); }
  };

  const genNarrative = async () => {
    if (!result) return;
    setGenBusy(true);
    try {
      const r = await pathways.narrative(result.inputs, result.results);
      setNarrative(r.narrative || '(LLM narrative not available — Emergent LLM key required.)');
      // If we saved this analysis to a matter, persist the narrative so it lands in the PDF.
      if (form.matter_id && r.narrative) {
        try { await pathways.saveNarrative(form.matter_id, r.narrative); } catch { /* noop */ }
      }
    } finally { setGenBusy(false); }
  };

  const downloadPdf = () => {
    if (!form.matter_id) {
      alert('Attach this analysis to a matter (set matter_id) before exporting as PDF.');
      return;
    }
    window.open(pathways.pdfUrl(form.matter_id), '_blank');
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-6" data-testid="pathway-advisor-page">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#2ECC9A]/15">
          <Compass className="h-4 w-4 text-[#2ECC9A]" />
        </div>
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Pathway advisor</h1>
          <div className="text-sm text-slate-500">Map a student profile to every eligible migration visa in one pass. Rules-based — every result links back to the official source.</div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[380px_1fr]">
        <Card className="border-slate-200" data-testid="pathway-form-card">
          <CardContent className="p-6">
            <div className="text-sm font-semibold text-[#1A3A5C]">Applicant profile</div>

            <div className="mt-4 space-y-3">
              <div>
                <Label className="text-xs">Attach to matter (optional)</Label>
                <Input
                  data-testid="pw-matter-id"
                  value={form.matter_id || ''}
                  onChange={(e) => set('matter_id', e.target.value.trim())}
                  className="mt-1 h-9 font-mono"
                  placeholder="matter id (enables PDF export)"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Age</Label>
                  <Input data-testid="pw-age" type="number" min="18" max="60" value={form.age} onChange={(e) => set('age', +e.target.value)} className="mt-1 h-9" />
                </div>
                <div>
                  <Label className="text-xs">Current visa</Label>
                  <Input data-testid="pw-current-visa" value={form.current_visa} onChange={(e) => set('current_visa', e.target.value)} className="mt-1 h-9" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Qualification</Label>
                  <Select value={form.qualification} onValueChange={(v) => set('qualification', v)}>
                    <SelectTrigger data-testid="pw-qualification" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{QUALIFICATIONS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">English</Label>
                  <Select value={form.english} onValueChange={(v) => set('english', v)}>
                    <SelectTrigger data-testid="pw-english" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{ENGLISH.map((e) => <SelectItem key={e.v} value={e.v}>{e.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Occupation (nominated)</Label>
                <Input data-testid="pw-occupation" value={form.occupation} onChange={(e) => set('occupation', e.target.value)} placeholder="e.g. Software Engineer" className="mt-1 h-9" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="text-xs">Total exp (yrs)</Label><Input data-testid="pw-exp-total" type="number" step="0.5" min="0" max="40" value={form.work_experience_years} onChange={(e) => set('work_experience_years', +e.target.value)} className="mt-1 h-9" /></div>
                <div><Label className="text-xs">AU study (yrs)</Label><Input data-testid="pw-study-au" type="number" step="0.5" min="0" max="10" value={form.australian_study_years} onChange={(e) => set('australian_study_years', +e.target.value)} className="mt-1 h-9" /></div>
                <div><Label className="text-xs">AU work (yrs)</Label><Input data-testid="pw-work-au" type="number" step="0.5" min="0" max="15" value={form.australian_work_years} onChange={(e) => set('australian_work_years', +e.target.value)} className="mt-1 h-9" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Target state</Label>
                  <Select value={form.state} onValueChange={(v) => set('state', v)}>
                    <SelectTrigger data-testid="pw-state" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Skills assessment</Label>
                  <Select value={form.skills_assessment_status} onValueChange={(v) => set('skills_assessment_status', v)}>
                    <SelectTrigger data-testid="pw-assessment" className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{ASSESSMENT.map((a) => <SelectItem key={a} value={a}>{a.replace('_', ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2 pt-1">
                {[
                  ['onshore', 'Currently onshore'],
                  ['regional_study', 'Studied in regional Australia'],
                  ['regional_work', 'Worked in regional Australia'],
                  ['partner_skills', 'Skilled partner (10 pts)'],
                  ['has_employer_sponsor', 'Has approved employer sponsor'],
                ].map(([k, l]) => (
                  <div key={k} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">{l}</span>
                    <Switch data-testid={`pw-${k}`} checked={form[k]} onCheckedChange={(v) => set(k, v)} />
                  </div>
                ))}
              </div>
              <Button data-testid="pw-analyse" onClick={analyse} disabled={busy} className="w-full gap-1 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Compass className="h-4 w-4" />}
                Analyse pathways
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {!result ? (
            <Card className="border-dashed border-slate-200">
              <CardContent className="flex flex-col items-center justify-center p-16 text-center">
                <Compass className="h-8 w-8 text-slate-300" />
                <div className="mt-3 text-sm font-medium text-[#1A3A5C]">Enter the profile and click Analyse</div>
                <div className="mt-1 text-xs text-slate-500">The engine evaluates 485, 482, 186, 190, 491, 191, 858 and DAMA in one pass.</div>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="border-slate-200" data-testid="pw-summary-card">
                <CardContent className="p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-[#1A3A5C]">Points estimate</div>
                      <div className="text-xs text-slate-500">Indicative — not a substitute for the DHA points test.</div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-[#1A3A5C]" data-testid="pw-points">{result.points_estimate.total}</div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400">total pts</div>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-1 md:grid-cols-3 text-[11px]">
                    {Object.entries(result.points_estimate.breakdown).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/50 px-2 py-1">
                        <span className="text-slate-500">{k.replace('_', ' ')}</span>
                        <span className="font-semibold text-[#1A3A5C]">{v}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Button data-testid="pw-narrative" onClick={genNarrative} disabled={genBusy} variant="outline" className="gap-1 border-slate-200 text-xs">
                      {genBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      Generate plain-English summary
                    </Button>
                    <Button
                      data-testid="pw-download-pdf"
                      onClick={downloadPdf}
                      disabled={!form.matter_id}
                      variant="outline"
                      className="gap-1 border-[#2ECC9A]/40 text-xs text-[#1A3A5C] hover:bg-[#2ECC9A]/10"
                      title={form.matter_id ? 'Download branded migration options PDF' : 'Attach a matter_id in the form to enable PDF export'}
                    >
                      <Download className="h-3 w-3" /> Download PDF
                    </Button>
                  </div>

                  {narrative && (
                    <div className="mt-3 rounded-md border border-[#2ECC9A]/30 bg-[#2ECC9A]/5 p-3 text-xs leading-relaxed text-slate-700" data-testid="pw-narrative-text">
                      {narrative.split('\n\n').map((p, i) => <p key={i} className={i > 0 ? 'mt-2' : ''}>{p}</p>)}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="grid gap-3 md:grid-cols-2" data-testid="pw-results">
                {result.results.map((r, i) => {
                  const Icon = VERDICT_ICON[r.eligible] || XCircle;
                  const tone = VERDICT_TONE[r.eligible] || VERDICT_TONE.no;
                  return (
                    <div key={r.visa} data-testid={`pw-result-${r.visa}`} className={`rounded-md border p-3 ${tone}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold text-[#1A3A5C]">{r.visa} · {r.label}</div>
                          <div className="mt-0.5 text-xs">{r.reason}</div>
                        </div>
                        <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                      </div>
                      {r.gaps && r.gaps.length > 0 && (
                        <ul className="mt-2 list-disc pl-4 text-[11px] text-slate-700">
                          {r.gaps.map((g, j) => <li key={j}>{g}</li>)}
                        </ul>
                      )}
                      {r.points_estimate && (
                        <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-600">
                          <Target className="h-3 w-3" /> {r.points_estimate.total} pts estimated
                        </div>
                      )}
                      <a href={r.source_url} target="_blank" rel="noopener noreferrer"
                         className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[#1A3A5C] hover:text-[#2ECC9A]">
                        Verify at source <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
